import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCopy, FaCheck } from 'react-icons/fa';
import './App.css';
import thalaPc from './files/thala_pc.jpg';
import thalaPh from './files/thala_ph.jpg';
import confetti from 'canvas-confetti';
import { checkReason } from './logic';

function App() {
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(typeof window !== 'undefined' ? window.innerWidth : 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const backgroundImage = isMobile ? thalaPh : thalaPc;

  useEffect(() => {
    if (reason && typeof window !== 'undefined') {
      const thalaColors = ['#f9d616', '#0057e7', '#ffffff', '#ffd700'];
      confetti({
        particleCount: 200,
        spread: 80,
        origin: { y: 0.6 },
        colors: thalaColors,
        shapes: ['circle']
      });

      const intervals = [
        setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 65, origin: { x: 0 }, colors: thalaColors }), 300),
        setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 65, origin: { x: 1 }, colors: thalaColors }), 600),
        setTimeout(() => confetti({ particleCount: 100, spread: 100, origin: { y: 0.8 }, colors: thalaColors }), 900)
      ];

      return () => intervals.forEach(clearTimeout);
    }
  }, [reason]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter a name or phrase');
      setReason('');
      return;
    }
    setError('');
    setIsLoading(true);
    setCopied(false);

    try {
      await new Promise(r => setTimeout(r, 600)); // Smooth loading simulation
      const data = checkReason(name);
      if (data.reason) {
        setReason(data.reason);
        setError('');
      } else {
        setError(data.error || 'Something went wrong');
        setReason('');
      }
    } catch (err) {
      setError('Failed to calculate. Please try again.');
      setReason('');
    } finally {
      setIsLoading(false);
    }
  }, [name]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(reason);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy!');
    }
  }, [reason]);

  return (
    <div 
      className="app-background"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.2) 60%, transparent 100%), url(${backgroundImage})`,
        backgroundPosition: isMobile ? 'center' : 'center right'
      }}
    >
      <div className={`wrapper ${isMobile ? 'mobile-wrapper' : 'desktop-wrapper'}`}>
        <motion.div 
          className="container" 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="header">
            <h1>Thala For A Reason</h1>
            <p className="subtitle">Discover why your Name is special!&nbsp;✨</p>
          </div>

          <form onSubmit={handleSubmit} className="form-container" autoComplete="off">
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter a name, word, or phrase..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-box"
                autoFocus
              />
              <motion.button
                type="submit"
                className="submit-button"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? <span className="spinner"></span> : 'Check Reason'}
              </motion.button>
            </div>
          </form>

          <div aria-live="polite" className="result-wrapper">
            <AnimatePresence mode="wait">
              {error && (
                <motion.div 
                  key="error"
                  className="result-container error" 
                  initial={{ opacity: 0, height: 0 }} 
                  animate={{ opacity: 1, height: 'auto' }} 
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="result-icon">⚠️</div>
                  <p className="error-message">{error}</p>
                </motion.div>
              )}
              {reason && (
                <motion.div 
                  key="success"
                  className="result-container success" 
                  initial={{ opacity: 0, scale: 0.95 }} 
                  animate={{ opacity: 1, scale: 1 }} 
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <div className="result-header">
                    <div className="result-icon">🎉</div>
                    <motion.button 
                      onClick={copyToClipboard} 
                      className={`copy-button ${copied ? 'copied' : ''}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      {copied ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy</>}
                    </motion.button>
                  </div>
                  <textarea readOnly value={reason} rows={5} className="result-box" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="footer">
            <p>Made with 💛 for Thala fans</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default App;